import { motion } from "framer-motion";
import { Clock, Calendar, ArrowRight, Info } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

const WorkingHoursSection = () => {
  const companyState = useSelector((state) => state?.companyReducer?.company) || {};
  const schedule = companyState?.workingSchedule || {};
  const workingDays = schedule.workingDays || [];
  const openHours = schedule.openHours || "N/A";
  const closeHours = schedule.closeHours || "N/A";
  const nav = useNavigate();

  const formattedDays = workingDays.map(
    (day) => day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
  );

  const daysOfWeek = [
    { name: "Mon", full: "Monday" },
    { name: "Tue", full: "Tuesday" },
    { name: "Wed", full: "Wednesday" },
    { name: "Thu", full: "Thursday" },
    { name: "Fri", full: "Friday" },
    { name: "Sat", full: "Saturday" },
    { name: "Sun", full: "Sunday" }
  ];

  //  12-hour format function
  const formatTime12Hour = (time) => {
    if (!time || time === "N/A") return { hour: "--", minute: "00", period: "" };

    let [hour, minute] = time.split(":").map(Number);

    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;

    return {
      hour: hour.toString().padStart(2, "0"),
      minute: (minute || 0).toString().padStart(2, "0"),
      period
    };
  };

  const openTime = formatTime12Hour(openHours);
  const closeTime = formatTime12Hour(closeHours);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16 px-4">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/70 backdrop-blur-md rounded-full shadow-sm border border-white/60 mb-4">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Business Hours</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            We're Here For{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              You
            </span>
          </h1>

          <p className="text-gray-600 max-w-xl mx-auto">
            Visit us during business hours or book ahead for guaranteed availability.
          </p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/65 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 overflow-hidden"
        >
          {/* Time Display */}
          <div className="bg-gradient-to-r from-blue-600/90 to-indigo-600/90 backdrop-blur-md p-8">
            <div className="flex items-center justify-center gap-12 flex-wrap">

              {/* Open */}
              <div className="text-center">
                <div className="text-blue-100 text-sm font-medium mb-2 tracking-wide">Opens</div>
                <div className="flex items-end gap-1 justify-center">
                  <span className="text-5xl font-bold text-white">{openTime.hour}</span>
                  <span className="text-3xl font-semibold text-blue-200">:{openTime.minute}</span>
                  <span className="text-lg font-semibold text-blue-200 ml-1">{openTime.period}</span>
                </div>
              </div>

              <div className="hidden sm:block">
                <div className="w-px h-16 bg-blue-400/30"></div>
              </div>

              {/* Close */}
              <div className="text-center">
                <div className="text-blue-100 text-sm font-medium mb-2 tracking-wide">Closes</div>
                <div className="flex items-end gap-1 justify-center">
                  <span className="text-5xl font-bold text-white">{closeTime.hour}</span>
                  <span className="text-3xl font-semibold text-blue-200">:{closeTime.minute}</span>
                  <span className="text-lg font-semibold text-blue-200 ml-1">{closeTime.period}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="p-8">
            <div className="flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">Weekly Schedule</h3>
            </div>

            <div className="grid grid-cols-7 gap-2">
              {daysOfWeek.map((day, i) => {
                const isWorking = formattedDays.includes(day.full);
                return (
                  <motion.div
                    key={day.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.05 }}
                    className="relative group"
                  >
                    <div
                      className={`aspect-square rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer ${
                        isWorking
                          ? "bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 hover:scale-105"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      <span className={`text-xs font-bold ${isWorking ? "text-white" : "text-gray-400"}`}>
                        {day.name}
                      </span>
                      <div className={`w-1.5 h-1.5 rounded-full mt-2 ${isWorking ? "bg-white" : "bg-gray-300"}`}></div>
                    </div>

                    {/* Tooltip */}
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      <div className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                        {day.full}
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {formattedDays.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                Schedule information not available
              </div>
            )}

            {/* Info Banner */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 flex items-start gap-3 p-4 bg-white/50 backdrop-blur-sm border border-blue-100/40 rounded-2xl"
            >
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-blue-700">Planning a visit?</span>{" "}
                  Morning hours typically have shorter wait times and better availability.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-600 mb-4 text-sm">Need assistance outside business hours?</p>
          <button
            onClick={() => nav("/contact")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/30 transition-all hover:shadow-xl hover:-translate-y-0.5"
          >
            Contact Support
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkingHoursSection;
